import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { X, Send, Paperclip, Check, CheckCheck, FileText, Image as ImageIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ReactionBar } from "@/components/messaging/ReactionBar";

interface Conversation {
  id: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
}

interface FloatingStaffChatProps {
  conversation: Conversation;
  onClose: () => void;
}

export function FloatingStaffChat({ conversation, onClose }: FloatingStaffChatProps) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [showReactions, setShowReactions] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch messages for this conversation
  const { data: messages = [] } = useQuery({
    queryKey: ['floating-staff-chat', conversation.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversation.id)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    refetchInterval: 10000,
  });

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark as read
  useEffect(() => {
    if (!user?.id) return;

    const markAsRead = async () => {
      const unread = messages.filter((m: any) => m.recipient_id === user.id && !m.read);
      for (const msg of unread) {
        await supabase
          .from('messages')
          .update({ read: true, read_at: new Date().toISOString() })
          .eq('id', msg.id);
      }
      if (unread.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['floating-messages'] });
      }
    };

    markAsRead();
  }, [messages, user?.id]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel(`floating-chat-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversation.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['floating-staff-chat', conversation.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation.id]);

  const handleSend = async () => {
    if (!content.trim() || sending) return;
    if (!user?.id || !profile) return;

    setSending(true);
    try {
      // supabase-js RESOLVES with { error } rather than throwing, so an insert
      // failure never reached the catch below — the input was cleared and the
      // message silently vanished. Surface it so the catch can do its job.
      const { error } = await supabase.from('messages').insert({
        conversation_id: conversation.id,
        sender_id: user.id,
        sender_name: profile.full_name || user.email || 'Unknown',
        recipient_id: conversation.recipientId,
        content: content.trim(),
      });
      if (error) throw error;

      setContent("");
      queryClient.invalidateQueries({ queryKey: ['floating-staff-chat', conversation.id] });
      queryClient.invalidateQueries({ queryKey: ['floating-messages'] });
    } catch (error) {
      console.error('Failed to send:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    if (!user?.id || !profile) return;

    const message = messages.find((m: any) => m.id === messageId);
    if (!message) return;

    const reactions = Array.isArray(message.reactions) ? message.reactions : [];
    const existingIndex = reactions.findIndex(
      (r: any) => r.emoji === emoji && r.user_email === user.email
    );

    let newReactions;
    if (existingIndex >= 0) {
      newReactions = reactions.filter((_: any, i: number) => i !== existingIndex);
    } else {
      newReactions = [...reactions, {
        emoji,
        user_email: user.email,
        user_name: profile.full_name || user.email,
      }];
    }

    await supabase
      .from('messages')
      .update({ reactions: newReactions })
      .eq('id', messageId);

    queryClient.invalidateQueries({ queryKey: ['floating-staff-chat', conversation.id] });
    setShowReactions(null);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="w-[calc(100vw-2rem)] sm:w-96 h-[500px] sm:h-[600px]"
    >
      <Card className="h-full flex flex-col shadow-2xl border-border/50 overflow-hidden">
        {/* Header */}
        <CardHeader className="pb-3 border-b bg-blue-600 text-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-white/20">
                <AvatarFallback className="bg-white/20 text-white">
                  {getInitials(conversation.recipientName)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold">{conversation.recipientName}</h3>
                <p className="text-xs opacity-80">{conversation.recipientEmail}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full" ref={scrollRef}>
            <div className="p-4 space-y-3">
              {messages.map((message: any) => {
                const isOwn = message.sender_id === user?.id;
                const reactions = Array.isArray(message.reactions) ? message.reactions : [];
                const groupedReactions = reactions.reduce((acc: any, r: any) => {
                  if (!acc[r.emoji]) acc[r.emoji] = { count: 0, users: [] };
                  acc[r.emoji].count++;
                  acc[r.emoji].users.push(r.user_name);
                  return acc;
                }, {});

                return (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    onMouseEnter={() => setShowReactions(message.id)}
                    onMouseLeave={() => setShowReactions(null)}
                  >
                    <div className="relative max-w-[85%]">
                      {/* Reaction Button */}
                      <AnimatePresence>
                        {showReactions === message.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className={`absolute -top-8 ${isOwn ? 'right-0' : 'left-0'}`}
                          >
                            <ReactionBar
                              onReact={(emoji) => handleReaction(message.id, emoji)}
                              position={isOwn ? 'left' : 'right'}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isOwn
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-muted rounded-bl-md'
                        }`}
                      >
                        {message.content && (
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        )}

                        {/* Attachments */}
                        {message.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {message.attachments.map((att: any, i: number) => (
                              <a
                                key={i}
                                href={att.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-xs ${
                                  isOwn ? 'text-white/80 hover:text-white' : 'text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {att.type?.startsWith('image/') ? (
                                  <ImageIcon className="h-3 w-3" />
                                ) : (
                                  <FileText className="h-3 w-3" />
                                )}
                                <span className="underline truncate">{att.name}</span>
                              </a>
                            ))}
                          </div>
                        )}

                        <div className={`flex items-center gap-1 mt-1 text-xs ${isOwn ? 'justify-end opacity-70' : 'opacity-50'}`}>
                          <span>{format(new Date(message.created_at), 'HH:mm')}</span>
                          {isOwn && (
                            message.read ? (
                              <CheckCheck className="h-3 w-3 text-blue-200" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )
                          )}
                        </div>
                      </div>

                      {/* Reactions */}
                      {Object.keys(groupedReactions).length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(groupedReactions).map(([emoji, data]: [string, any]) => (
                            <button
                              key={emoji}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-muted text-xs hover:bg-muted/80"
                              onClick={() => handleReaction(message.id, emoji)}
                              title={data.users.join(', ')}
                            >
                              <span>{emoji}</span>
                              <span>{data.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>

        {/* Input */}
        <div className="border-t p-3 shrink-0">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Type a message..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              className="flex-1"
            />
            <Button
              onClick={handleSend}
              disabled={!content.trim() || sending}
              size="icon"
              className="shrink-0 bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
